import {
  Archive,
  ArchiveRestore,
  Boxes,
  Layers3,
  LoaderCircle,
  MapPin,
  PackagePlus,
  Pencil,
  Plus,
  Save,
  X,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import {
  createDeployment,
  getDeployments,
  updateDeployment,
} from "../api";
import { formatRelativeTime } from "../lib/format";
import type {
  DeploymentItem,
  QodeItem,
  TimelineEvent,
  WorkspaceCapabilities,
} from "../types";

type DeploymentsViewProps = {
  capabilities: WorkspaceCapabilities;
  qodes: QodeItem[];
  workspaceId: string;
  onQodeChanged: (qode: QodeItem, event?: TimelineEvent | null) => void;
};

const currency = new Intl.NumberFormat("zh-CN", {
  style: "currency",
  currency: "CNY",
  maximumFractionDigits: 2,
});

export function DeploymentsView({
  capabilities,
  qodes,
  workspaceId,
  onQodeChanged,
}: DeploymentsViewProps) {
  const [deployments, setDeployments] = useState<DeploymentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [qodeId, setQodeId] = useState("");
  const [name, setName] = useState("");
  const [mediumType, setMediumType] = useState("poster");
  const [location, setLocation] = useState("");
  const [batchReference, setBatchReference] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [replacementCost, setReplacementCost] = useState("0");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editQuantity, setEditQuantity] = useState("1");
  const [editReplacementCost, setEditReplacementCost] = useState("0");
  const [editReason, setEditReason] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setDeployments([]);
    getDeployments()
      .then((response) => {
        if (active) setDeployments(response.deployments);
      })
      .catch((loadError) => {
        if (active) {
          setError(
            loadError instanceof Error ? loadError.message : "无法读取投放批次。",
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [workspaceId]);

  useEffect(() => {
    if (!qodeId && qodes.length) {
      setQodeId(qodes[0].id);
    }
  }, [qodeId, qodes]);

  const totals = useMemo(
    () =>
      deployments.reduce(
        (result, deployment) => ({
          quantity:
            result.quantity +
            (deployment.status === "active" ? deployment.quantity : 0),
          replacementCost:
            result.replacementCost +
            (deployment.status === "active"
              ? deployment.replacementCostCents
              : 0),
          active: result.active + Number(deployment.status === "active"),
        }),
        { quantity: 0, replacementCost: 0, active: 0 },
      ),
    [deployments],
  );

  const resetForm = () => {
    setName("");
    setMediumType("poster");
    setLocation("");
    setBatchReference("");
    setQuantity("1");
    setReplacementCost("0");
  };

  const submitDeployment = async () => {
    setWorking(true);
    setError("");
    try {
      const response = await createDeployment({
        qodeId,
        name: name.trim(),
        mediumType: mediumType.trim(),
        location: location.trim(),
        batchReference: batchReference.trim(),
        quantity: Number(quantity),
        replacementCostCents: Math.round(Number(replacementCost) * 100),
        deployedAt: new Date().toISOString(),
      });
      setDeployments((current) => [response.deployment, ...current]);
      onQodeChanged(response.qode, response.event);
      resetForm();
      setFormOpen(false);
    } catch (actionError) {
      setError(
        actionError instanceof Error ? actionError.message : "投放登记失败。",
      );
    } finally {
      setWorking(false);
    }
  };

  const valid =
    qodeId &&
    name.trim() &&
    mediumType.trim() &&
    location.trim() &&
    batchReference.trim() &&
    Number.isInteger(Number(quantity)) &&
    Number(quantity) > 0 &&
    Number(replacementCost) >= 0;

  const startEditing = (deployment: DeploymentItem) => {
    setEditingId(deployment.id);
    setEditQuantity(String(deployment.quantity));
    setEditReplacementCost(
      String(deployment.replacementCostCents / 100),
    );
    setEditReason("");
    setError("");
  };

  const submitAdjustment = async (
    deployment: DeploymentItem,
    status = deployment.status,
  ) => {
    setWorking(true);
    setError("");
    try {
      const response = await updateDeployment(deployment.id, {
        quantity: Number(editQuantity),
        replacementCostCents: Math.round(
          Number(editReplacementCost) * 100,
        ),
        status,
        reason: editReason.trim(),
      });
      setDeployments((current) =>
        current.map((item) =>
          item.id === response.deployment.id ? response.deployment : item,
        ),
      );
      onQodeChanged(response.qode, response.event);
      setEditingId(null);
      setEditReason("");
    } catch (actionError) {
      setError(
        actionError instanceof Error ? actionError.message : "批次调整失败。",
      );
    } finally {
      setWorking(false);
    }
  };

  const adjustmentValid =
    Number.isInteger(Number(editQuantity)) &&
    Number(editQuantity) > 0 &&
    Number(editReplacementCost) >= 0 &&
    editReason.trim().length >= 2;

  return (
    <div className="page deployments-page">
      <section className="page-heading">
        <div>
          <h1>Deployments</h1>
          <p>实体入口批次、位置与替换成本。</p>
        </div>
        {capabilities.operate ? (
          <button
            className="primary-button"
            type="button"
            onClick={() => setFormOpen((current) => !current)}
          >
            {formOpen ? <X size={16} /> : <Plus size={16} />}
            {formOpen ? "关闭" : "登记批次"}
          </button>
        ) : null}
      </section>

      <section className="deployment-summary">
        <div>
          <Boxes size={17} />
          <span>批次</span>
          <strong>{deployments.length}</strong>
        </div>
        <div>
          <Layers3 size={17} />
          <span>实体入口</span>
          <strong>{totals.quantity.toLocaleString("zh-CN")}</strong>
        </div>
        <div>
          <MapPin size={17} />
          <span>有效位置</span>
          <strong>{totals.active}</strong>
        </div>
        <div>
          <PackagePlus size={17} />
          <span>替换成本</span>
          <strong>{currency.format(totals.replacementCost / 100)}</strong>
        </div>
      </section>

      {formOpen && capabilities.operate ? (
        <section className="deployment-create">
          <div className="deployment-create-grid">
            <label>
              Qode
              <select
                value={qodeId}
                onChange={(event) => setQodeId(event.target.value)}
              >
                {qodes.map((qode) => (
                  <option value={qode.id} key={qode.id}>
                    {qode.name} · /r/{qode.slug}
                  </option>
                ))}
              </select>
            </label>
            <label>
              批次名称
              <input
                value={name}
                maxLength={80}
                placeholder="门店海报第二批"
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label>
              媒介
              <select
                value={mediumType}
                onChange={(event) => setMediumType(event.target.value)}
              >
                <option value="poster">海报</option>
                <option value="package-insert">包装内页</option>
                <option value="table-card">桌卡</option>
                <option value="signage">标识牌</option>
                <option value="label">标签</option>
              </select>
            </label>
            <label>
              位置
              <input
                value={location}
                maxLength={120}
                placeholder="上海门店"
                onChange={(event) => setLocation(event.target.value)}
              />
            </label>
            <label>
              批次编号
              <input
                value={batchReference}
                maxLength={80}
                placeholder="SH-2026-02"
                onChange={(event) => setBatchReference(event.target.value)}
              />
            </label>
            <label>
              数量
              <input
                value={quantity}
                min="1"
                step="1"
                type="number"
                onChange={(event) => setQuantity(event.target.value)}
              />
            </label>
            <label>
              替换成本（元）
              <input
                value={replacementCost}
                min="0"
                step="0.01"
                type="number"
                onChange={(event) => setReplacementCost(event.target.value)}
              />
            </label>
            <button
              className="primary-button"
              type="button"
              disabled={working || !valid}
              onClick={() => void submitDeployment()}
            >
              {working ? (
                <LoaderCircle className="spin" size={15} />
              ) : (
                <Plus size={15} />
              )}
              登记
            </button>
          </div>
        </section>
      ) : null}

      {error ? <div className="action-error">{error}</div> : null}

      <section className="deployment-table">
        <div className="deployment-table-head">
          <span>Qode / 批次</span>
          <span>媒介 / 位置</span>
          <span>数量</span>
          <span>替换成本</span>
          <span>状态 / 时间</span>
          <span>操作</span>
        </div>
        {loading ? (
          <div className="drawer-loading">
            <LoaderCircle className="spin" size={18} />
            正在读取投放批次
          </div>
        ) : null}
        {!loading && !deployments.length ? (
          <div className="deployment-empty">
            <Boxes size={20} />
            暂无投放批次
          </div>
        ) : null}
        {!loading
          ? deployments.map((deployment) => (
              <Fragment key={deployment.id}>
                <article className="deployment-row">
                  <div className="deployment-identity">
                    <strong>{deployment.qode.name}</strong>
                    <span>{deployment.name}</span>
                    <code>{deployment.batchReference}</code>
                  </div>
                  <div className="deployment-place">
                    <strong>{deployment.mediumType}</strong>
                    <span>{deployment.location}</span>
                  </div>
                  <strong className="deployment-quantity">
                    {deployment.quantity.toLocaleString("zh-CN")}
                  </strong>
                  <span>
                    {currency.format(deployment.replacementCostCents / 100)}
                  </span>
                  <div className="deployment-state">
                    <span className={`deployment-status ${deployment.status}`}>
                      <i />
                      {deployment.status === "active" ? "有效" : "已退役"}
                    </span>
                    <small>
                      {deployment.revisionCount} 个版本 ·{" "}
                      {formatRelativeTime(deployment.lastChangedAt)}
                    </small>
                  </div>
                  <button
                    className="icon-button deployment-edit-button"
                    type="button"
                    title={`调整 ${deployment.name}`}
                    aria-label={`调整 ${deployment.name}`}
                    disabled={!capabilities.operate || working}
                    onClick={() =>
                      editingId === deployment.id
                        ? setEditingId(null)
                        : startEditing(deployment)
                    }
                  >
                    {editingId === deployment.id ? (
                      <X size={15} />
                    ) : (
                      <Pencil size={15} />
                    )}
                  </button>
                </article>
                {editingId === deployment.id ? (
                  <div className="deployment-adjust">
                    <label>
                      数量
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={editQuantity}
                        onChange={(event) =>
                          setEditQuantity(event.target.value)
                        }
                      />
                    </label>
                    <label>
                      替换成本（元）
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editReplacementCost}
                        onChange={(event) =>
                          setEditReplacementCost(event.target.value)
                        }
                      />
                    </label>
                    <label className="deployment-adjust-reason">
                      调整原因
                      <input
                        value={editReason}
                        maxLength={300}
                        placeholder="盘点后调整数量"
                        onChange={(event) =>
                          setEditReason(event.target.value)
                        }
                      />
                    </label>
                    <button
                      className="secondary-button"
                      type="button"
                      disabled={working || !adjustmentValid}
                      onClick={() => void submitAdjustment(deployment)}
                    >
                      {working ? (
                        <LoaderCircle className="spin" size={14} />
                      ) : (
                        <Save size={14} />
                      )}
                      保存调整
                    </button>
                    <button
                      className={
                        deployment.status === "active"
                          ? "danger-button"
                          : "secondary-button"
                      }
                      type="button"
                      disabled={working || !adjustmentValid}
                      onClick={() =>
                        void submitAdjustment(
                          deployment,
                          deployment.status === "active"
                            ? "retired"
                            : "active",
                        )
                      }
                    >
                      {deployment.status === "active" ? (
                        <Archive size={14} />
                      ) : (
                        <ArchiveRestore size={14} />
                      )}
                      {deployment.status === "active"
                        ? "退役批次"
                        : "重新启用"}
                    </button>
                  </div>
                ) : null}
              </Fragment>
            ))
          : null}
      </section>
    </div>
  );
}
